import dayjs from "dayjs";
import { IProject } from "../../@types";
import { formatCurrencyMoney } from "../format";
import AddModal from "../../components/Invoice/sections/modal/AddModal";
import { DeleteProject } from "./deleteProject";

export const columns = [
  {
    key: "project_name",
    label: "Project",
    className: "border-x border-x-gray-200",
    width: 15,
  },
  {
    key: "customer",
    label: "Customer",
    className: "border-x border-x-gray-200",
    width: 13,
  },
  {
    key: "status",
    label: "Status",
    className: "border-x border-x-gray-200",
    width: 12,
  },

  {
    key: "income_expense",
    label: "Income vs. Expense",
    className: "border-x border-x-gray-200",
    width: 30,
  },
  {
    key: "profit_margin",
    label: "Profit Margin",
    className: "border-x border-x-gray-200",
    width: 17,
  },
  {
    key: "action",
    label: "Action",
    className: "border-x border-x-gray-200",
    width: 14,
  },
];

export const statusColors: any = {
  "not-started": {
    text: "#344054",
    bg: "#F2F4F7",
  },
  "in-progress": {
    text: "#044E97",
    bg: "#E0EEFE",
  },
  completed: {
    text: "#039855",
    bg: "#D1FADF",
  },
  cancelled: {
    text: "#D92D20",
    bg: "#FEF3F2",
  },
};

const getStatusLabel = (status: string): string => {
  switch (status?.toLowerCase()) {
    case "not-started":
      return "Not Started";
    case "in-progress":
      return "In Progress";
    case "cancelled":
      return "Cancelled";
    case "completed":
      return "Completed";
    default:
      return "Secondary";
  }
};

export const renderProjectsData = (
  projects: IProject[],
  onDeleteClick: (project: IProject) => void,
) => {
  return projects.map((project: IProject, index: number) => {
    let margin = 0;
    let profitMargin = 0;
    let profit = 0;

    const income = project?.income ?? 0;
    const expense = project?.expense ?? 0;

    if (income > 0) {
      margin = (expense * 100) / income;
      profitMargin = 100 - margin;
      profit = income - expense;

      // Clamp to avoid overflows due to bad data
      margin = Math.min(Math.max(margin, 0), 100);
      profitMargin = Math.min(Math.max(profitMargin, 0), 100);
    }

    return {
      createdAt: (
        <a href={`/projects/${project.id}/view`} className="xl:text-violet-800">
          {dayjs(project.createdAt).format("MM/DD/YYYY")}
        </a>
      ),
      customer: (
        <a
          href={`/customers/${project.customerId}/view`}
          className="text-[#044E97] font-medium"
        >
          {project.customer || "NA"}
        </a>
      ),
      companyId: project.companyId,
      id: project.id,
      project_name: (
        <a
          className="text-[#044E97] font-semibold text-sm cursor-pointer"
          href={`/projects/${project.id}/view`}
        >
          {project.name || "NA"}
        </a>
      ),
      action: (
        <>
          <div className="flex space-x-5">
            <button
              type="button"
              onClick={() => {
                onDeleteClick(project);
              }}
              className="text-[#D92D20] font-medium text-sm"
            >
              Delete
            </button>
          </div>
        </>
      ),
      status: (
        <div className="flex">
          <span
            className={` px-2 py-1 rounded-[4px] text-[14px] text-sm font-medium capitalize flex justify-center bg-[${statusColors[project.status]?.bg}] text-[${statusColors[project.status]?.text}]`}
          >
            {getStatusLabel(project.status)}
          </span>
        </div>
      ),
      income: `${formatCurrencyMoney(project.income)}`,
      expense: `${formatCurrencyMoney(project.expense)}`,
      profit_margin: `${profit == 0 ? `${profitMargin.toFixed(2)}%` : `${formatCurrencyMoney(profit)} (${profitMargin.toFixed(2)}%)`} `,
      income_expense: (
        <div className="flex flex-col gap-3 w-full">
          <IncomeProgressBar amount={income} profitMargin={profitMargin} />
          <ExpenseProgressBar
            amount={expense}
            margin={margin}
            profitMargin={profitMargin}
          />
        </div>
      ),
    };
  });
};

const IncomeProgressBar = ({
  amount,
  profitMargin,
}: {
  amount: number;
  profitMargin: number;
}) => {
  return (
    <div className="flex items-center justify-between w-full px-2 mt-2">
      <div className="flex-shrink-0 pr-4 w-[15%]">
        <h1 className="text-[#1D2939] font-medium text-[14px]">Income</h1>
      </div>

      <div className="text-right w-auto pl-4">
        <h1 className="text-[#1D2939] font-medium text-[14px]">
          {formatCurrencyMoney(amount)}
        </h1>
      </div>
    </div>
  );
};
const ExpenseProgressBar = ({
  amount,
  margin,
  profitMargin,
}: {
  amount: number;
  margin: number;
  profitMargin: number;
}) => {
  return (
    <div className="mt-[1px] flex justify-between items-center px-2 w-full mb-2">
      <div className="flex-shrink-0 pr-4 w-[15%]">
        <h1 className="text-[#1D2939] font-medium text-[14px]">Expense</h1>
      </div>

      <div className="text-right w-auto pl-4">
        <h1 className="text-[#1D2939] font-medium text-[14px]">
          {formatCurrencyMoney(amount)}
        </h1>
      </div>
    </div>
  );
};